import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Card,
  message,
  Spin,
  Input,
  Select,
  Space,
  Statistic,
  Row,
  Col,
  Breadcrumb,
  Modal,
} from "antd";
import {
  ApartmentOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getOrganizationChart,
  getHierarchyStats,
  recalculateHierarchy,
  assignManager,
  HierarchyUser,
} from "../../utils/api/hierarchyAPI";
import OrganizationChart from "./components/OrganizationChart";
import AssignManagerModal from "./components/AssignManagerModal";
import { usePermissions } from "../../utils/usePermissions";
import { isSuperAdmin } from "../../utils/permissions";
import "./Hierarchy.scss";

const { Option } = Select;
const { Search } = Input;

const Hierarchy: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userData = useSelector((state: any) => state.userData.user);
  const { hasPermission } = usePermissions();

  const isSuper = isSuperAdmin(userData?.roles);
  const canView = isSuper || hasPermission("hierarchy:view");
  const canAssignManager = isSuper || hasPermission("hierarchy:assign-manager");
  const canRecalculate = isSuper || hasPermission("hierarchy:recalculate");
  const canViewStats = isSuper || hasPermission("hierarchy:view-stats");

  const storedUser = React.useMemo(() => {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      console.error("Failed to parse stored user", error);
      return null;
    }
  }, []);

  const clientId =
    (typeof userData?.clientId === "object"
      ? userData?.clientId?._id
      : userData?.clientId) ||
    (typeof storedUser?.clientId === "object"
      ? storedUser?.clientId?._id
      : storedUser?.clientId);

  const [loading, setLoading] = useState(false);
  const [hierarchyData, setHierarchyData] = useState<HierarchyUser[]>([]);
  const [filteredData, setFilteredData] = useState<HierarchyUser[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterLevel, setFilterLevel] = useState<number | "all">("all");
  const [stats, setStats] = useState<any>(null);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<HierarchyUser | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Load organization chart
  const loadOrganizationChart = useCallback(async () => {
    if (!clientId) return;

    setLoading(true);
    try {
      const [chartData, statsData] = await Promise.all([
        getOrganizationChart(clientId),
        getHierarchyStats(clientId),
      ]);

      setHierarchyData(chartData.data);
      setFilteredData(chartData.data);
      setStats(statsData);
    } catch (error: any) {
      console.error("Error loading hierarchy:", error);
      message.error(
        error.response?.data?.message || t("Failed to load organization chart")
      );
    } finally {
      setLoading(false);
    }
  }, [clientId, t]);

  useEffect(() => {
    loadOrganizationChart();
  }, [loadOrganizationChart]);

  // Filter and search
  useEffect(() => {
    let filtered = hierarchyData;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const filterRecursive = (users: HierarchyUser[]): HierarchyUser[] => {
        return users
          .map((user) => {
            const matchesSearch =
              user.firstName?.toLowerCase().includes(term) ||
              user.lastName?.toLowerCase().includes(term) ||
              user.email?.toLowerCase().includes(term) ||
              user.position?.positionName?.toLowerCase().includes(term);

            const filteredChildren = user.children
              ? filterRecursive(user.children)
              : [];

            if (matchesSearch || filteredChildren.length > 0) {
              return {
                ...user,
                children:
                  filteredChildren.length > 0
                    ? filteredChildren
                    : user.children,
              };
            }
            return null;
          })
          .filter(Boolean) as HierarchyUser[];
      };

      filtered = filterRecursive(filtered);
    }

    // Level filter
    if (filterLevel !== "all") {
      const filterByLevel = (users: HierarchyUser[]): HierarchyUser[] => {
        return users
          .map((user) => {
            if (user.hierarchyLevel === filterLevel) {
              return user;
            }
            if (user.children) {
              const filteredChildren = filterByLevel(user.children);
              if (filteredChildren.length > 0) {
                return {
                  ...user,
                  children: filteredChildren,
                };
              }
            }
            return null;
          })
          .filter(Boolean) as HierarchyUser[];
      };

      filtered = filterByLevel(filtered);
    }

    setFilteredData(filtered);
  }, [searchTerm, filterLevel, hierarchyData]);

  // Handle assign manager
  const handleAssignManager = (user: HierarchyUser) => {
    setSelectedUser(user);
    setAssignModalVisible(true);
  };

  const handleAssignManagerSubmit = async (managerId: string | null) => {
    if (!selectedUser) return;

    try {
      await assignManager(selectedUser._id, managerId);
      message.success(t("Manager assigned successfully"));
      setAssignModalVisible(false);
      loadOrganizationChart(); // Reload
    } catch (error: any) {
      console.error("Error assigning manager:", error);
      message.error(
        error.response?.data?.message || t("Failed to assign manager")
      );
    }
  };

  // Recalculate hierarchy
  const handleRecalculate = async () => {
    if (!clientId) return;

    Modal.confirm({
      title: t("Recalculate Hierarchy"),
      content: t(
        "This will recalculate all hierarchy levels. Do you want to continue?"
      ),
      onOk: async () => {
        setRefreshing(true);
        try {
          await recalculateHierarchy(clientId);
          message.success(t("Hierarchy recalculated successfully"));
          loadOrganizationChart();
        } catch (error: any) {
          console.error("Error recalculating:", error);
          message.error(
            error.response?.data?.message ||
              t("Failed to recalculate hierarchy")
          );
        } finally {
          setRefreshing(false);
        }
      },
    });
  };

  // Get max level for filter options
  const maxLevel = stats?.stats?.maxLevel || 5;
  const levelOptions = Array.from({ length: maxLevel + 1 }, (_, i) => i);

  // Permission check - block access if user doesn't have permission
  if (!canView) {
    return (
      <div className="mainContainer">
        <Card>
          <div style={{ textAlign: "center", padding: "40px" }}>
            <ApartmentOutlined style={{ fontSize: "48px", color: "#ccc" }} />
            <h2>{t("No Permission")}</h2>
            <p>
              {t("You don't have permission to view the organization chart")}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="hierarchy-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <ApartmentOutlined className="page-icon" />
          <div>
            <h1>{t("Organization Chart")}</h1>
            <Breadcrumb>
              <Breadcrumb.Item onClick={() => navigate("/")}>
                {t("Dashboard")}
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("Organization Chart")}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined spin={refreshing} />}
            onClick={handleRecalculate}
            disabled={!canRecalculate}
            loading={refreshing}
          >
            {t("Recalculate")}
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => message.info("Export functionality coming soon")}
          >
            {t("Export")}
          </Button>
        </Space>
      </div>

      {/* Statistics Cards */}
      {stats && canViewStats && (
        <Row gutter={[16, 16]} className="stats-row">
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title={t("Total Employees")}
                value={stats.stats.totalEmployees || 0}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title={t("Total Managers")}
                value={stats.stats.totalManagers || 0}
                prefix={<TeamOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title={t("Avg Team Size")}
                value={stats.stats.avgTeamSize?.toFixed(1) || 0}
                suffix="employees"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title={t("Max Hierarchy Levels")}
                value={stats.stats.maxLevel + 1 || 1}
                suffix="levels"
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card className="filters-card">
        <Space size="middle" wrap>
          <Search
            placeholder={t("Search by name, email, or position")}
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select
            value={filterLevel}
            onChange={setFilterLevel}
            style={{ width: 200 }}
            placeholder={t("Filter by level")}
          >
            <Option value="all">{t("All Levels")}</Option>
            {levelOptions.map((level) => (
              <Option key={level} value={level}>
                {level === 0 ? t("Top Level") : `${t("Level")} ${level}`}
              </Option>
            ))}
          </Select>
          {(searchTerm || filterLevel !== "all") && (
            <Button
              onClick={() => {
                setSearchTerm("");
                setFilterLevel("all");
              }}
            >
              {t("Clear Filters")}
            </Button>
          )}
        </Space>
      </Card>

      {/* Organization Chart */}
      <Card className="chart-card">
        {loading ? (
          <div className="loading-container">
            <Spin size="large" />
            <p>{t("Loading organization chart...")}</p>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="empty-state">
            <ApartmentOutlined style={{ fontSize: 64, color: "#d9d9d9" }} />
            <h3>{t("No employees found")}</h3>
            <p>
              {searchTerm || filterLevel !== "all"
                ? t("Try adjusting your filters")
                : t("Start by adding employees to your organization")}
            </p>
          </div>
        ) : (
          <OrganizationChart
            data={filteredData}
            onAssignManager={canAssignManager ? handleAssignManager : undefined}
            onUserClick={(user: HierarchyUser) =>
              navigate(`/users/${user._id}`)
            }
          />
        )}
      </Card>

      {/* Assign Manager Modal */}
      <AssignManagerModal
        visible={assignModalVisible}
        user={selectedUser}
        onCancel={() => setAssignModalVisible(false)}
        onSubmit={handleAssignManagerSubmit}
        clientId={clientId}
      />
    </div>
  );
};

export default Hierarchy;
